import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { MouseEvent, useState } from 'react';
import { Button, Confirm, useNotify, useRecordContext, useRefresh, useUpdate } from 'react-admin';

type CampaignArchiveRecord = {
  archivedAt?: string | null;
  campaignNumber?: number | null;
  id: string;
  name?: string | null;
};

const formatCampaignArchiveLabel = (record: CampaignArchiveRecord) => {
  if (record.campaignNumber && record.name) return `#${record.campaignNumber} ${record.name}`;
  if (record.campaignNumber) return `#${record.campaignNumber}`;
  return record.name ?? 'this campaign';
};

export const CampaignArchiveButton = () => {
  const record = useRecordContext<CampaignArchiveRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);

  const isArchived = Boolean(record?.archivedAt);
  const action = isArchived ? 'Restore' : 'Archive';

  const [update, { isLoading }] = useUpdate(
    'campaigns',
    undefined,
    {
      onSuccess: () => {
        notify(`Campaign ${action.toLowerCase()}d successfully`, { type: 'success' });
        refresh();
      },
      onError: error => {
        notify(`Error: ${error}`, { type: 'warning' });
      },
    },
  );

  if (!record) {
    return null;
  }

  const handleClick = (event: MouseEvent) => {
    event.stopPropagation();
    setOpen(true);
  };

  return (
    <>
      <Button label={action} onClick={handleClick} disabled={isLoading} color={isArchived ? 'info' : 'error'}>
        {isArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
      </Button>
      <Confirm
        isOpen={open}
        loading={isLoading}
        title={`Confirm Campaign ${action}`}
        content={`Are you sure you want to ${action.toLowerCase()} ${formatCampaignArchiveLabel(record)}?`}
        onConfirm={() => {
          update('campaigns', {
            id: record.id,
            data: { archivedAt: isArchived ? null : new Date().toISOString() },
            previousData: record,
          });
          setOpen(false);
        }}
        onClose={() => setOpen(false)}
      />
    </>
  );
};
